import React, { useState, useEffect, useRef } from 'react';
import styles from './Icd10.module.scss';
import Table from '../../../components/table/Table';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import DropDownButton from '../../../components/dropDownButton/DropDownButton';
import Button from '../../../components/button/Button';
import Modal from '../../../components/modal/Modal';
import UploadForm from '../../../components/uploadForm/UploadForm';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import Icd10FormModal from './Icd10FormModal';

const ICD10_TEMPLATE_URL = 'https://maestrostoragedev.blob.core.windows.net/maestro/files/icd-10-template.xlsx';

const Icd10 = () => {
  const { showToast } = useToasts();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(100);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteIds, setDeleteIds] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const progressRef = useRef(0);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    start: 1,
    end: 1,
    hasPrev: false,
    hasNext: false
  });

  const fetchIcd10 = React.useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await axiosClient.get('/icd10/list', {
        params: { page, limit: 10, search }
      });
      const responseData = response.data;

      setData(responseData.data || []);
      setSelectedRows([]);

      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < responseData.totalPages
      });
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, search]);

  useEffect(() => {
    fetchIcd10(1);
  }, [fetchIcd10]);

  const handlePageChange = (newPage) => {
    fetchIcd10(newPage);
  };

  const handleCreate = async (formData) => {
    setIsSubmitting(true);
    try {
      const response = await axiosClient.post('/icd10/create', formData);
      showToast(response.message);
      setShowAddModal(false);
      fetchIcd10(1);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (formData) => {
    setIsSubmitting(true);
    try {
      const response = await axiosClient.patch(`/icd10/${editRecord.id}`, formData);
      showToast(response.message);
      setEditRecord(null);
      fetchIcd10(pagination.current);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      const response = await axiosClient.delete('/icd10/delete', { data: { ids: deleteIds } });
      showToast(response.message);
      setDeleteIds(null);
      fetchIcd10(pagination.current);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showProgress = () => {
    setUploadProgress(0);
    progressRef.current = 0;
    const interval = setInterval(() => {
      progressRef.current = progressRef.current + 1;
      setUploadProgress(progressRef.current);
      if (progressRef.current >= 100) {
        clearInterval(interval);
      }
    }, 10);
    return () => clearInterval(interval);
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('icd10File', file);
    setIsUploadLoading(true);
    showProgress();
    try {
      const response = await axiosClient.post('/icd10/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast(response.message);
      setShowUploadModal(false);
      fetchIcd10(1);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsUploadLoading(false);
    }
  };

  const columns = [
    { header: 'Code', key: 'code' },
    { header: 'Short Description', key: 'shortDescription' },
    { header: 'Long Description', key: 'longDescription' },
  ];

  const getActionItems = (row) => [
    { label: 'Edit', onClick: () => setEditRecord(row) },
    { label: 'Delete', onClick: () => setDeleteIds([row.id]) },
  ];

  const dropdownItems = [
    { label: 'Add New', onClick: () => setShowAddModal(true) },
    { label: 'Bulk Upload', onClick: () => setShowUploadModal(true) },
    ...(selectedRows.length > 0 ? [{ label: `Delete Selected (${selectedRows.length})`, onClick: () => setDeleteIds(selectedRows) }] : []),
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="ICD 10" />
        <div className={styles.headerActions}>
          <Search onSubmit={(value) => setSearch(value)} />
          <DropDownButton
            label="Manage ICD-10"
            items={dropdownItems}
          />
        </div>
      </div>
      <div className={styles.container}>
        <Table
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={handlePageChange}
          getActionItems={getActionItems}
          onRowSelect={setSelectedRows}
          selectedRows={selectedRows}
        />
      </div>

      {showAddModal && (
        <Icd10FormModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleCreate}
          isLoading={isSubmitting}
        />
      )}

      {editRecord && (
        <Icd10FormModal
          initialData={editRecord}
          onClose={() => setEditRecord(null)}
          onSubmit={handleEdit}
          isLoading={isSubmitting}
        />
      )}

      {showUploadModal && (
        <Modal>
          <UploadForm
            isUploading={isUploadLoading}
            uploadProgress={uploadProgress}
            onClose={() => setShowUploadModal(false)}
            onSubmit={handleUpload}
            buttonLabel="Import ICD-10"
            templateFileUrl={ICD10_TEMPLATE_URL}
          />
        </Modal>
      )}

      {deleteIds && (
        <Modal>
          {isSubmitting && <LoadingOverlay />}
          <div className={styles.deleteModal}>
            <h2 className={styles.deleteTitle}>
              {deleteIds.length > 1
                ? `Are you sure you want to delete ${deleteIds.length} ICD-10 codes?`
                : 'Are you sure you want to delete this ICD-10 code?'}
            </h2>
            <div className={styles.deleteActions}>
              <Button label="Delete" size="small" variant="secondary" onClick={handleDelete} />
              <Button label="Cancel" size="small" variant="cancel" onClick={() => setDeleteIds(null)} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Icd10;
